import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge, EffectiveBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { MagnifyingGlass, Plus, Truck, GasPump, DownloadSimple, Check, Warning, Circle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { MaintenanceBadge, MotBadge, TaxBadge } from "../components/MaintDims";
import { HipOnboardDialog, OffHireDialog, HipBadge, OffHireBadge, HireReviewBadge, AddLolerDialog, HipInfo } from "../components/HipDialogs";
import { fmtDate } from "../lib/date";

const GROUP_LABELS = { vehicle: "Vehicles", plant: "Plant", tool: "Tools", other: "Other" };
const GROUP_ORDER = ["vehicle", "plant", "tool", "other"];
const MEASURE_MILES = ["miles", "km"];
const MEASURE_HOURS = ["engine_hours", "operating_hours", "hours"];

function RegPlateInline({ reg }) {
  if (!reg) return null;
  return <span data-testid="card-reg-plate" className="inline-flex items-center font-mono font-black tracking-widest text-sm bg-yellow-400 text-black px-2 py-0.5 rounded border-2 border-yellow-500">{reg}</span>;
}

// Newest valid recorded reading (current_mileage/current_hours are the authoritative applied value).
function meterDisplay(a) {
  const u = a.meter_unit || a.measurement_unit;
  if (MEASURE_MILES.includes(u)) {
    const v = a.current_mileage;
    if (v == null) return { text: "Mileage not recorded", muted: true };
    return { text: `${Number(v).toLocaleString("en-GB")} ${u === "km" ? "km" : "mi"}` };
  }
  if (MEASURE_HOURS.includes(u)) {
    const v = a.current_hours;
    if (v == null) return { text: "Hours not recorded", muted: true };
    return { text: `${Number(v).toLocaleString("en-GB")} h` };
  }
  return null;
}

function RegMeterRow({ a }) {
  const meter = meterDisplay(a);
  if (!a.registration && !meter) return null;
  return (
    <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mt-1">
      <RegPlateInline reg={a.registration} />
      {a.registration && meter && <span className="text-slate-300 select-none" aria-hidden="true">•</span>}
      {meter && (
        <span data-testid={`asset-meter-${a.id}`}
          className={`font-mono text-xs ${meter.muted ? "text-slate-400 italic" : "text-slate-600 font-semibold"}`}>
          {meter.text}
        </span>
      )}
    </div>
  );
}

const fuelCls = (t) => {
  const k = (t || "").toLowerCase();
  if (k === "petrol") return "bg-emerald-600 text-white";
  if (k === "diesel") return "bg-slate-900 text-white";
  if (k === "electric") return "bg-blue-600 text-white";
  if (k.includes("hybrid")) return "bg-teal-600 text-white";
  if (k === "lpg" || k === "hvo") return "bg-amber-500 text-slate-900";
  return "bg-slate-500 text-white";
};

function CheckLine({ lc, testid }) {
  if (!lc) return <span data-testid={testid} className="inline-flex items-center gap-1 text-xs text-slate-400"><Circle size={12} weight="bold" />Check required</span>;
  const defects = (lc.defect_count || 0) > 0 || lc.outcome === "fail";
  const when = lc.completed_at ? new Date(lc.completed_at) : null;
  const today = when && when.toDateString() === new Date().toDateString();
  const timeTxt = when ? (today ? when.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : fmtDate(lc.completed_at)) : "";
  if (defects) {
    return <span data-testid={testid} className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700"><Warning size={12} weight="bold" />{lc.defect_count > 0 ? `${lc.defect_count} defect${lc.defect_count > 1 ? "s" : ""} reported` : "Defects reported"}{timeTxt ? ` · ${timeTxt}` : ""}</span>;
  }
  return <span data-testid={testid} className="inline-flex items-center gap-1 text-xs text-emerald-700"><Check size={12} weight="bold" />{today ? "Checked today · nil defects" : "Nil defects reported"}{timeTxt ? ` · ${timeTxt}` : ""}</span>;
}

function AssetCard({ a, canManage, onRestore, onAssign, onOffHire, onReview }) {
  const border = a.archived ? "#64748b" : a.is_hired ? "#6366f1" : a.status?.code === "out_of_service" ? "#dc2626" : a.status?.blocks_use ? "#f59e0b" : "#10b981";
  return (
    <div className={`bg-white border-2 border-slate-300 rounded-lg border-l-8 ${a.archived ? "opacity-90" : ""}`} style={{ borderLeftColor: border }}>
      <Link to={`/assets/${a.id}`} data-testid={`asset-card-${a.id}`} className="block p-4 hover:bg-slate-50 transition-colors duration-100">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-mono text-xs font-bold text-slate-500">{a.asset_number}{a.asset_type_name ? ` · ${a.asset_type_name}` : ""}</div>
            <div className="font-body font-semibold text-slate-900">{a.name}</div>
            <RegMeterRow a={a} />
            {a.is_hired && <HipInfo a={a} />}
          </div>
          <Truck size={24} className="text-slate-300" weight="bold" />
        </div>
        {a.archived ? (
          <div className="mt-3 flex flex-wrap gap-1.5 items-center">
            {a.is_hired
              ? <span data-testid={`asset-offhired-${a.id}`} className="inline-flex items-center gap-1 text-xs font-bold uppercase rounded px-1.5 py-0.5 bg-indigo-800 text-white">Off-hired</span>
              : <span data-testid={`asset-disposed-${a.id}`} className="inline-flex items-center gap-1 text-xs font-bold uppercase rounded px-1.5 py-0.5 bg-slate-800 text-white">Disposed{a.disposal_method ? ` · ${a.disposal_method.replace("_", " ")}` : ""}</span>}
            {a.disposal_date && <span className="font-mono text-xs text-slate-500">{fmtDate(a.disposal_date)}</span>}
          </div>
        ) : (
          <div className="mt-3 flex flex-wrap gap-1.5 items-center">
            {a.is_hired && <HipBadge />}
            {a.is_hired && <OffHireBadge hire={a.current_hire} />}
            {a.is_hired && <HireReviewBadge hire={a.current_hire} />}
            <EffectiveBadge eff={a.effective_status} />
            <MaintenanceBadge ms={a.maintenance_status} />
            {a.road_compliance && <MotBadge mot={a.road_compliance.mot} />}
            {a.road_compliance && <TaxBadge tax={a.road_compliance.tax} />}
            {a.fuel_type && <span data-testid={`asset-fuel-${a.id}`} className={`inline-flex items-center gap-1 text-xs font-bold uppercase rounded px-1.5 py-0.5 ${fuelCls(a.fuel_type)}`}><GasPump size={12} weight="bold" />{a.fuel_type}</span>}
          </div>
        )}
        {!a.archived && <div className="mt-2"><CheckLine lc={a.last_check} testid={`asset-check-${a.id}`} /></div>}
      </Link>
      {a.archived && canManage && !a.is_hired && (
        <div className="px-4 pb-3">
          <Button data-testid={`restore-asset-${a.id}`} onClick={() => onRestore(a)} className="h-8 bg-emerald-600 hover:bg-emerald-700 font-semibold text-xs">Restore to register</Button>
        </div>
      )}
      {!a.archived && canManage && a.is_hired && (
        <div className="px-4 pb-3 flex items-center gap-2 border-t border-slate-100 pt-2">
          <Link to={`/assets/${a.id}`} className="flex-1"><Button data-testid={`hip-open-${a.id}`} className="h-8 w-full bg-slate-100 text-slate-700 hover:bg-slate-200 font-semibold text-xs">Open</Button></Link>
          {a.current_hire?.review_due && <Button data-testid={`hip-review-${a.id}`} onClick={() => onReview(a)} className="h-8 bg-purple-600 hover:bg-purple-700 font-semibold text-xs">Review LOLER</Button>}
          <Button data-testid={`hip-offhire-${a.id}`} onClick={() => onOffHire(a)} className="h-8 bg-red-600 hover:bg-red-700 font-semibold text-xs">Off Hire</Button>
        </div>
      )}
      {!a.archived && canManage && !a.is_hired && (
        <div className="px-4 pb-3 flex items-center justify-between gap-2 border-t border-slate-100 pt-2">
          <span className="text-xs text-slate-500 truncate">{a.assigned_person_name ? <>Assigned: <span className="font-semibold text-slate-700">{a.assigned_person_name}</span></> : "Unassigned"}</span>
          <Button data-testid={`assign-asset-${a.id}`} onClick={() => onAssign(a)} className="h-7 bg-slate-100 text-slate-700 hover:bg-slate-200 font-semibold text-xs shrink-0">Offer custody</Button>
        </div>
      )}
    </div>
  );
}

function AssetForm({ types, onCreated, canManage, autoOpen }) {
  const [open, setOpen] = useState(!!autoOpen);
  const [holder, setHolder] = useState("me");
  const [toId, setToId] = useState("");
  const [recips, setRecips] = useState([]);
  const [autoNum, setAutoNum] = useState("");
  const [override, setOverride] = useState(false);
  const [form, setForm] = useState({ asset_number: "", name: "", asset_type_id: "", registration: "", serial_number: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => {
    if (!open) return;
    setOverride(false);
    setHolder("me"); setToId("");
    api.get("/asset-transfers/recipients").then((r) => setRecips(r.data)).catch(() => setRecips([]));
    setForm({ asset_number: "", name: "", asset_type_id: "", registration: "", serial_number: "" });
    api.get("/assets/next-number").then((r) => setAutoNum(r.data.next_number)).catch(() => setAutoNum(""));
  }, [open]);
  const submit = async () => {
    const payload = { ...form };
    if (!override) delete payload.asset_number;  // let the server allocate the next number atomically
    try {
      const { data: a } = await api.post("/assets", payload);
      if (holder === "offer") {
        try {
          await api.post("/asset-transfers/offer", { asset_id: a.id, to_profile_id: toId, is_new_asset: true });
          toast.success(`Asset ${a.asset_number} created — awaiting acceptance`);
        } catch (e) { toast.error(`Asset ${a.asset_number} created, but the offer failed: ${e.response?.data?.detail || "error"}`); }
      } else toast.success(`Asset ${a.asset_number} created`);
      setOpen(false);
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-asset-button" className="bg-slate-900 hover:bg-slate-800 font-semibold"><Plus size={18} weight="bold" className="mr-1" />New Asset</Button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="font-display">New Asset</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-3">
          {(!override || !canManage) ? (
            <div>
              <div className="flex items-center justify-between border-2 border-slate-200 rounded px-3 py-2 bg-slate-50">
                <span data-testid="asset-form-auto-number" className="font-mono font-bold text-slate-800">{autoNum || "…"}</span>
                {canManage && <button type="button" data-testid="asset-number-override-toggle" onClick={() => { setOverride(true); set("asset_number", autoNum); }} className="text-xs text-blue-600 font-semibold">Enter manually</button>}
              </div>
              <p className="text-xs text-slate-500 mt-1">Asset Number: <b>{autoNum || "…"}</b> · Automatically allocated</p>
            </div>
          ) : (
            <div>
              <Input data-testid="asset-form-number" placeholder="Asset number *" value={form.asset_number} onChange={(e) => set("asset_number", e.target.value)} />
              <p className="text-xs text-amber-600 mt-1">The system normally allocates Asset Numbers automatically. A manual number must be unique and this override is audited. <button type="button" onClick={() => { setOverride(false); set("asset_number", ""); }} className="text-blue-600 font-semibold">Use auto</button></p>
            </div>
          )}
          <Input data-testid="asset-form-name" placeholder="Name *" value={form.name} onChange={(e) => set("name", e.target.value)} />
          <select data-testid="asset-form-type" className="h-10 border-2 border-slate-300 rounded px-2" value={form.asset_type_id} onChange={(e) => set("asset_type_id", e.target.value)}>
            <option value="">Select type…</option>
            {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <Input data-testid="asset-form-reg" placeholder="Registration (if applicable)" value={form.registration} onChange={(e) => set("registration", e.target.value)} />
          <Input data-testid="asset-form-serial" placeholder="Serial number (if applicable)" value={form.serial_number} onChange={(e) => set("serial_number", e.target.value)} />
          <div className="flex flex-col gap-2 border-2 border-slate-200 rounded p-2" data-testid="asset-form-holder">
            <span className="text-xs font-semibold text-slate-600">Who will hold this asset?</span>
            <label className="flex items-center gap-2 text-sm"><input type="radio" data-testid="asset-form-holder-me" checked={holder === "me"} onChange={() => setHolder("me")} />{canManage ? "No one yet (stays unassigned)" : "Keep it myself"}</label>
            <label className="flex items-center gap-2 text-sm"><input type="radio" data-testid="asset-form-holder-offer" checked={holder === "offer"} onChange={() => setHolder("offer")} />Offer it to someone else (they must accept)</label>
            {holder === "offer" && (
              <select data-testid="asset-form-offer-to" className="h-10 border-2 border-slate-300 rounded px-2" value={toId} onChange={(e) => setToId(e.target.value)}>
                <option value="">Select person…</option>
                {recips.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
          </div>
          <Button data-testid="asset-form-submit" onClick={submit} disabled={!form.name || (override && !form.asset_number) || (holder === "offer" && !toId)} className="bg-blue-600 hover:bg-blue-700 font-semibold">Create</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AssetsPage() {
  const { hasPerm } = useAuth();
  const [assets, setAssets] = useState([]);
  const [types, setTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("number");
  const [fGroup, setFGroup] = useState("");
  const [fType, setFType] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [fScope, setFScope] = useState("top_level");
  const [sp] = useSearchParams();
  const [view, setView] = useState(sp.get("view") === "hired" ? "hired" : "owned"); // owned | hired | hired_archived
  const [fOffHire, setFOffHire] = useState(sp.get("offhire") || ""); // "" | overdue | due_soon
  const [hipOpen, setHipOpen] = useState(false);
  const [offHireFor, setOffHireFor] = useState(null);
  const [reviewFor, setReviewFor] = useState(null);
  const insFilter = sp.get("insurance");

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (showArchived || view === "hired_archived") p.set("include_archived", "true");
    p.set("scope", fScope);
    api.get(`/assets?${p.toString()}`).then((r) => setAssets(r.data)).catch(() => {});
  }, [q, showArchived, view, fScope]);

  const restore = async (a) => {
    try { await api.post(`/assets/${a.id}/restore`, {}); toast.success("Asset restored to register"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const [people, setPeople] = useState([]);
  const [assignFor, setAssignFor] = useState(null);
  const [assignId, setAssignId] = useState("");
  const canManageAssets = hasPerm("asset_manager", "administrator");
  useEffect(() => { if (canManageAssets) api.get("/asset-transfers/recipients").then((r) => setPeople(r.data)).catch(() => {}); }, [canManageAssets]);
  const openAssign = (a) => { setAssignFor(a); setAssignId(""); };
  const saveAssign = async () => {
    try { await api.post("/asset-transfers/offer", { asset_id: assignFor.id, to_profile_id: assignId }); toast.success("Offer sent — custody changes when they accept"); setAssignFor(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/config/asset-types").then((r) => setTypes(r.data)).catch(() => {});
    api.get("/config/asset-statuses").then((r) => setStatuses(r.data)).catch(() => {});
  }, []);

  const num = (a) => { const n = parseInt(a.asset_number, 10); return Number.isNaN(n) ? Infinity : n; };
  const cmp = (a, b) => {
    if (sort === "name") return (a.name || "").localeCompare(b.name || "");
    if (sort === "status") return (a.status?.name || "").localeCompare(b.status?.name || "");
    if (sort === "mot") return (a.road_compliance?.mot?.expiry || "9999").localeCompare(b.road_compliance?.mot?.expiry || "9999");
    if (sort === "tax") return (a.road_compliance?.tax?.due_date || "9999").localeCompare(b.road_compliance?.tax?.due_date || "9999");
    return num(a) - num(b) || (a.asset_number || "").localeCompare(b.asset_number || "");
  };
  const visible = assets
    .filter((a) => {
      if (view === "hired") return a.is_hired && !a.archived && (!fOffHire || a.current_hire?.off_hire_state === fOffHire);
      if (view === "hired_archived") return a.is_hired && a.archived;
      return !a.is_hired && (showArchived ? a.archived : !a.archived);
    })
    .filter((a) => (!fGroup || (a.asset_group || "other") === fGroup) && (!fType || a.asset_type_id === fType) && (!fStatus || a.status?.code === fStatus))
    .filter((a) => (insFilter !== "uninsured" || (a.compliance_locks || []).some((l) => l.source === "insurance") || (a.insurance && a.insurance.lock)))
    .slice().sort(cmp);
  const groupsPresent = GROUP_ORDER.filter((g) => visible.some((a) => (a.asset_group || "other") === g));
  const selCls = "h-11 border-2 border-slate-300 rounded px-2 text-sm bg-white";

  const exportFleet = () => {
    const esc = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const cols = ["Asset Number", "Name", "Type", "Group", "Registration", "Recorded Status", "Effective Status",
      "Mileage", "Hours", "MOT Expiry", "MOT State", "Tax State", "Tax Due", "SORN", "Insurance State", "Insurance Policy", "Assigned To"];
    const rows = visible.map((a) => [
      a.asset_number, a.name, a.asset_type_name, a.asset_group, a.registration,
      a.status?.name, a.effective_status?.name, a.current_mileage, a.current_hours,
      a.road_compliance?.mot?.expiry, a.road_compliance?.mot?.state,
      a.road_compliance?.tax?.state, a.road_compliance?.tax?.due_date, a.sorn ? "Yes" : "",
      a.insurance?.state, a.insurance?.policy?.policy_number || a.insurance?.policy?.name || "",
      a.assigned_person_name,
    ]);
    const csv = [cols, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = document.createElement("a"); link.href = url; link.download = "fleet.csv"; link.click(); URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} asset${rows.length === 1 ? "" : "s"}`);
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Asset Register</h1>
        <div className="flex gap-2 items-center flex-wrap">
          <Link data-testid="asset-tree-link" to="/assets/tree" className="h-11 inline-flex items-center px-3 rounded font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200">Asset Tree</Link>
          {view === "owned" && <Button data-testid="toggle-archived" onClick={() => setShowArchived((v) => !v)} className={`h-11 font-semibold ${showArchived ? "bg-slate-900 text-white hover:bg-slate-800" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>{showArchived ? "Showing archived" : "Show archived"}</Button>}
          {canManageAssets && <Button data-testid="fleet-export" variant="outline" onClick={exportFleet} className="h-11 font-semibold"><DownloadSimple size={16} weight="bold" className="mr-1" />Export CSV</Button>}
          {hasPerm("asset_manager", "administrator") && view !== "owned" && <Button data-testid="add-hip-button" onClick={() => setHipOpen(true)} className="h-11 bg-indigo-600 hover:bg-indigo-700 font-semibold"><Plus size={18} weight="bold" className="mr-1" />Add Hired-In Plant</Button>}
          {view === "owned" && <AssetForm key={sp.get("new") || "form"} types={types} canManage={canManageAssets} autoOpen={sp.get("new") === "1"} onCreated={load} />}
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200" data-testid="asset-view-tabs">
        {[["owned", "Owned Assets"], ["hired", "Hired In"], ["hired_archived", "Previous Hires"]].map(([v, label]) => (
          <button key={v} data-testid={`asset-view-${v}`} onClick={() => setView(v)}
            className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${view === v ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{label}</button>
        ))}
      </div>

      {insFilter === "uninsured" && (
        <div data-testid="uninsured-filter-banner" className="bg-red-50 border-l-8 border-red-600 rounded p-3 flex items-center justify-between gap-2">
          <span className="font-body text-sm font-semibold text-red-800">Showing vehicles/assets with no valid insurance cover.</span>
          <Link to="/assets" className="text-sm text-blue-600 font-semibold">Clear filter</Link>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="asset-search" placeholder="Search number, reg, serial…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-10 h-11 border-2" />
        </div>
        <select data-testid="asset-filter-group" className={selCls} value={fGroup} onChange={(e) => setFGroup(e.target.value)}>
          <option value="">All groups</option>{GROUP_ORDER.map((g) => <option key={g} value={g}>{GROUP_LABELS[g]}</option>)}
        </select>
        <select data-testid="asset-filter-type" className={selCls} value={fType} onChange={(e) => setFType(e.target.value)}>
          <option value="">All types</option>{types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select data-testid="asset-filter-status" className={selCls} value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
          <option value="">All statuses</option>{statuses.map((s) => <option key={s.id} value={s.code}>{s.name}</option>)}
        </select>
        {view === "owned" && (
          <select data-testid="asset-filter-scope" className={selCls} value={fScope} onChange={(e) => setFScope(e.target.value)} title="Containment scope">
            <option value="top_level">Top-level only</option>
            <option value="all">Show all (incl. contained)</option>
            <option value="contained">Contained items</option>
            <option value="uncontained">Uncontained</option>
          </select>
        )}
        {view === "hired" && (
          <select data-testid="asset-filter-offhire" className={selCls} value={fOffHire} onChange={(e) => setFOffHire(e.target.value)}>
            <option value="">All hires</option>
            <option value="due_soon">Off-hire due soon</option>
            <option value="overdue">Off-hire overdue</option>
          </select>
        )}
        <select data-testid="asset-sort" className={selCls} value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="number">Sort: Number</option><option value="name">Sort: Name</option>
          <option value="status">Sort: Status</option><option value="mot">Sort: MOT expiry</option><option value="tax">Sort: Tax due</option>
        </select>
      </div>

      {groupsPresent.map((g) => {
        const items = visible.filter((a) => (a.asset_group || "other") === g);
        return (
          <div key={g} data-testid={`asset-group-${g}`}>
            <h2 className="font-display text-xl font-black text-slate-800 mb-2 flex items-center gap-2">
              {GROUP_LABELS[g]} <span className="font-mono text-sm text-slate-400">{items.length}</span>
            </h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((a) => <AssetCard key={a.id} a={a} canManage={hasPerm("asset_manager", "administrator")} onRestore={restore} onAssign={openAssign} onOffHire={setOffHireFor} onReview={setReviewFor} />)}
            </div>
          </div>
        );
      })}
      {visible.length === 0 && <p className="text-slate-400">No assets found.</p>}

      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">Offer {assignFor?.asset_number} · {assignFor?.name}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Currently with: <b>{assignFor?.assigned_person_name || "no one"}</b>. Custody only changes when the recipient accepts.</p>
          <select data-testid="assign-person-select" className="h-11 border-2 border-slate-300 rounded px-2" value={assignId} onChange={(e) => setAssignId(e.target.value)}>
            <option value="">Select person…</option>
            {people.filter((p) => p.id !== assignFor?.assigned_person_id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <Button data-testid="assign-save" disabled={!assignId} onClick={saveAssign} className="bg-blue-600 hover:bg-blue-700 font-semibold">Send offer</Button>
        </DialogContent>
      </Dialog>

      <HipOnboardDialog open={hipOpen} types={types} onClose={() => setHipOpen(false)}
        onDone={(res) => { setHipOpen(false); if (res?.asset_id) { window.location.href = `/assets/${res.asset_id}`; } else { setView("hired"); load(); } }} />
      {offHireFor && <OffHireDialog asset={offHireFor} open={!!offHireFor} onClose={() => setOffHireFor(null)} onDone={() => { setOffHireFor(null); load(); }} />}
      {reviewFor && <AddLolerDialog asset={reviewFor} review open={!!reviewFor} onClose={() => setReviewFor(null)} onDone={() => { setReviewFor(null); load(); }} />}
    </div>
  );
}
